/**
 * Parse `#floor-stack` unit numbers for public tower / available-units UIs.
 * Mirrors `parseUnitNumber` in `SuperPickerApp.tsx`.
 *
 * Ground-floor terrace houses often use FloorNumber "G" with unit numbers
 * like "#68" (no hyphen). Treat G as floor 1 for grid placement and rewrite
 * those to "#01-68" / "01-68" so stack can be derived. Callers that display
 * floor for "G" units should still show "-" (see `isGroundFloorNumber`).
 */
export function parseUnitNumber(
  unitNumber: string | null | undefined,
  floorNumber?: string | null,
): { floor: number | null; stack: number | null; floorLabel: string | null } {
  let normalized = String(unitNumber ?? '').trim();
  const floorRaw = String(floorNumber ?? '').trim().toUpperCase();
  if (floorRaw === 'G' && normalized && !normalized.includes('-')) {
    const hasHash = normalized.startsWith('#');
    const rest = (hasHash ? normalized.slice(1) : normalized).trim();
    if (rest) {
      normalized = `${hasHash ? '#' : ''}01-${rest}`;
    }
  }
  const match = normalized.match(/#?\s*(B?)(\d+)-(\d+)/i);
  if (match) {
    const isBasement = match[1].toUpperCase() === 'B';
    const parsedFloor = parseInt(match[2], 10);
    return {
      floor: isBasement ? -parsedFloor : parsedFloor,
      stack: parseInt(match[3], 10),
      floorLabel: isBasement ? `B${parsedFloor}` : String(parsedFloor),
    };
  }
  return { floor: null, stack: null, floorLabel: null };
}

/** True when the raw ProjectUnit FloorNumber is ground ("G"). */
export function isGroundFloorNumber(floorNumber?: string | null): boolean {
  return String(floorNumber ?? '').trim().toUpperCase() === 'G';
}
